import webglHome from 'config/webgl-home';

import type { DepartmentType } from 'app/data/Enums';
import type { Point } from 'app/contracts/Geometry';
import type { TimelineItem } from 'app/views/home/timeline/TimelineItem';
import type {
	HomeItemGeometry,
	HomeStageSlots,
	HomeWebGL,
	HomeWebGLDeps,
	HomeWebGLFrame,
	HomeWebGLSizes,
	HomeWebGLType
} from 'app/views/home/webgl/webgl.types';

/**
 * DebugWebGL — the 'debug' implementation of the home WebGL layer
 * (?webgl=debug). This is the seam acceptance test AND the living
 * documentation of the minimal HomeWebGL contract implementation:
 *
 *  - It imports NOTHING from webgl/particles/ or src/particles/ and reads no
 *    shared registries beyond what the contract hands it (slots / sizes /
 *    deps / frame / geometry). If this file ever needs such an import, the
 *    seam has a hole — fix the contract, not this constraint.
 *  - Everything it renders is 4 flat PIXI.Texture.WHITE sprites, one per
 *    department zone, positioned purely from HomeWebGLSizes.zones and the
 *    per-tick HomeWebGLFrame. All state changes are HARD CUTS (no tweens) so
 *    every contract event is visible as a discrete jump.
 *  - The screens/spaces sprites are CLIPPED to their maskRegion shapes: the
 *    home item config entries tagged `"maskRegion": "screens"|"spaces"` (via
 *    HomeItemGeometry.getMaskRegionItems) define rects the mask graphics
 *    re-trace from the items' live sprite bounds every tick — the items
 *    parallax, so the masks follow. Home/systems stay unmasked.
 *  - PRELOAD HANDSHAKE: create() fires reportPixelDataReady() +
 *    reportSceneCreated() + onReady() synchronously — mandatory, or
 *    PreloadController's load join never completes and the site never
 *    leaves the loader (see the contract JSDoc in webgl.types).
 */

type DebugDepartmentKey = 'home' | 'screens' | 'spaces' | 'systems';

const DEBUG_DEPARTMENTS: ReadonlyArray<DebugDepartmentKey> = ['home', 'screens', 'spaces', 'systems'];

//Departments whose sprite is clipped to a maskRegion shape (the region name
//doubles as the department key — the home item config tags the TV frames 'screens' and
//the spaces box 'spaces'). Home/systems render unmasked:
const MASKED_DEPARTMENTS: ReadonlyArray<DebugDepartmentKey> = ['screens', 'spaces'];

const DEPARTMENT_TINTS: Record<DebugDepartmentKey, number> = {
	home: 0x34dccd,
	screens: 0xf24a8d,
	spaces: 0x63b4b5,
	systems: 0xffb035
};

//Timeline region ids -> the department sprite to tint-flash (other region
//ids — e.g. SpacesBoxArea — are intentionally ignored):
const REGION_TO_DEPARTMENT: { [id: string]: DebugDepartmentKey } = {
	Home: 'home',
	HomeLogoArea: 'home',
	Screens: 'screens',
	Spaces: 'spaces',
	Systems: 'systems'
};

const FOCUS_ALPHA = 1;
const DIM_ALPHA = 0.15; //unfocused while another department has focus
const REST_ALPHA = 0.4; //no department focused (progress between bands)
const FOCUS_SCALE = 1.3;
const ZONE_SIDE_RATIO = 0.4; //sprite side = min(zone real px dims) * this
const WORK_DROP_TICKS = 45; //frames the resetFromWork y-offset stays before the hard cut back

export class DebugWebGL implements HomeWebGL {
	public readonly type: HomeWebGLType = 'debug';

	private _deps: HomeWebGLDeps;
	private _container: PIXI.Container;
	private _geometry: HomeItemGeometry;
	private _sprites: Record<DebugDepartmentKey, PIXI.Sprite>;
	//Per-department sub-containers the sprites live in (the masked departments
	//get their mask applied on the sub-container, not the sprite):
	private _departmentContainers: Record<DebugDepartmentKey, PIXI.Container>;
	//Per masked department: the mask graphic and the tagged items whose live
	//sprite bounds it re-traces each tick (items resolved lazily on the first
	//tick — create() runs between the two item passes):
	private _masks: Partial<
		Record<DebugDepartmentKey, { graphic: PIXI.Graphics; items: ReadonlyArray<TimelineItem> | null }>
	>;
	private _sizes: HomeWebGLSizes;
	private _sides: Record<DebugDepartmentKey, number>; //base sprite side px per department (resize())

	private _frozen = false;
	private _inIntro = false; //introStart..introComplete: intro methods own alphas, tick() keeps hands off
	private _introHomeBoost = 1; //introHover/introOut home-sprite scale
	private _exploreBoost = 1; //onExploreHover/Out home-sprite scale
	private _flashedSprites: Array<PIXI.Sprite> = []; //tint-flashed this tick, restored next tick
	private _workDrop: { department: DebugDepartmentKey; offY: number; ticksLeft: number } = null;

	constructor(deps: HomeWebGLDeps) {
		this._deps = deps;
	}

	//spritesheet is contract-required but unused: proof the minimal
	//implementation needs no texture atlas. geometry IS used — but only its
	//maskRegion surface (getMaskRegionItems), the item→WebGL config seam.
	public create(
		slots: HomeStageSlots,
		spritesheet: PIXI.Spritesheet,
		sizes: HomeWebGLSizes,
		geometry: HomeItemGeometry
	): void {
		this._geometry = geometry;
		this._container = new PIXI.Container();
		this._container.name = 'DebugWebGL';

		this._sprites = {} as Record<DebugDepartmentKey, PIXI.Sprite>;
		this._departmentContainers = {} as Record<DebugDepartmentKey, PIXI.Container>;
		this._masks = {};
		DEBUG_DEPARTMENTS.forEach((department) => {
			const departmentContainer = new PIXI.Container();
			departmentContainer.name = `debug-${department}-department`;

			const sprite = new PIXI.Sprite(PIXI.Texture.WHITE);
			sprite.name = `debug-${department}`;
			sprite.tint = DEPARTMENT_TINTS[department];
			sprite.anchor.set(0.5);
			sprite.alpha = REST_ALPHA;
			departmentContainer.addChild(sprite);

			this._sprites[department] = sprite;
			this._departmentContainers[department] = departmentContainer;
			this._container.addChild(departmentContainer);

			//Pixi mask rule (mirrors ParticlesStage's screensOutter/spacesOutter
			//mounting): the mask Graphics must itself be in the render tree, as
			//a sibling of the container it masks:
			if (MASKED_DEPARTMENTS.indexOf(department) !== -1) {
				const graphic = new PIXI.Graphics();
				graphic.name = `debug-${department}-mask`;
				graphic.interactive = false;
				departmentContainer.mask = graphic;
				this._container.addChild(graphic);
				this._masks[department] = { graphic: graphic, items: null };
			}
		});

		slots.webglMain.addChild(this._container);
		this.resize(sizes, false);

		//The mandatory preload handshake — synchronous, nothing to wait for:
		this._deps.reportPixelDataReady();
		this._deps.reportSceneCreated();
		this._deps.onReady();
	}

	public tick(frame: HomeWebGLFrame): void {
		if (this._frozen || !this._sprites || !this._sizes) {
			return;
		}

		//Hard-cut back any tint flash from the previous tick:
		this._flashedSprites.forEach((sprite) => {
			const department = sprite.name.replace('debug-', '') as DebugDepartmentKey;
			sprite.tint = DEPARTMENT_TINTS[department];
		});
		this._flashedSprites.length = 0;

		const zones = this._sizes.zones;
		if (!zones || !zones.home) {
			return;
		}

		const focus = this._focusFromProgress(frame.progress);

		DEBUG_DEPARTMENTS.forEach((department) => {
			const zone = zones[department];
			const sprite = this._sprites[department];

			//Zone centre: cx is relative to the virtual canvas width, cy to the
			//window height (TimelineZones._setContentArea semantics); containerX
			//converts virtual x to screen x:
			sprite.x = frame.containerX + zone.cx * this._sizes.virtualWidth;
			sprite.y = zone.cy * this._sizes.innerHeight;

			let scale = department === focus ? FOCUS_SCALE : 1;
			if (department === 'home') {
				scale *= this._introHomeBoost * this._exploreBoost;
			}
			const side = (this._sides ? this._sides[department] : 0) * scale;
			sprite.width = sprite.height = side;

			//During the intro the intro methods own the alphas:
			if (!this._inIntro) {
				sprite.alpha = focus ? (department === focus ? FOCUS_ALPHA : DIM_ALPHA) : REST_ALPHA;
			}
		});

		//resetFromWork drop-in: the department sprite sits at its y offset for a
		//visible beat, then hard-cuts back (positions above already reset it):
		if (this._workDrop) {
			this._sprites[this._workDrop.department].y += this._workDrop.offY;
			if (--this._workDrop.ticksLeft <= 0) {
				this._workDrop = null;
			}
		}

		this._updateMasks();
	}

	public resize(sizes: HomeWebGLSizes, firstResize: boolean): void {
		this._sizes = sizes;

		//Early-boot resizes arrive before TimelineZones has populated the zones:
		if (!sizes.zones || !sizes.zones.home) {
			return;
		}

		this._sides = {} as Record<DebugDepartmentKey, number>;
		DEBUG_DEPARTMENTS.forEach((department) => {
			const zone = sizes.zones[department];
			this._sides[department] = Math.min(zone.realPxWidth, zone.realPxHeight) * ZONE_SIDE_RATIO;
		});
	}

	public freeze(): void {
		this._frozen = true;
	}

	public unfreeze(): void {
		this._frozen = false;
	}

	public show(): void {
		if (this._container) {
			this._container.visible = true;
		}
	}

	public hide(): void {
		if (this._container) {
			this._container.visible = false;
		}
	}

	/** Re-centre / clear focus state; positions re-derive on the next tick. */
	public reset(): void {
		this._workDrop = null;
		this._introHomeBoost = 1;
		this._exploreBoost = 1;
		this._cutAlphas(() => REST_ALPHA);
	}

	public resetFromWork(department: DepartmentType, offY: number): void {
		this._workDrop = { department: department as DebugDepartmentKey, offY: offY, ticksLeft: WORK_DROP_TICKS };
	}

	public deeplink(department: DepartmentType | null): void {
		this._inIntro = false;
		if (department) {
			this._cutAlphas((key) => (key === department ? FOCUS_ALPHA : 0));
		} else {
			this.reset();
		}
	}

	public setupForWork(department: DepartmentType): void {
		this.hide();
	}

	public showFromWork(department: DepartmentType): void {
		this._inIntro = false;
		this.show();
		this._cutAlphas((key) => (key === department ? FOCUS_ALPHA : DIM_ALPHA));
	}

	/** No swap-side work-transition visuals in the debug scene. */
	public updateWorkTransitionParticles(introXPx: number, introProgressRatio: number): void {}

	public introStart(): void {
		this._inIntro = true;
		this._cutAlphas(() => 0);
	}

	public introHover(): void {
		this._introHomeBoost = 1.1;
	}

	public introOut(): void {
		this._introHomeBoost = 1;
	}

	public introComplete(): void {
		this._inIntro = false;
		this._cutAlphas(() => REST_ALPHA);
	}

	//Region events flash the department sprite white for one tick — a visible
	//one-frame confirmation the region stream reaches the layer:
	public onRegionEnter(id: string): void {
		this._flashRegion(id);
	}

	public onRegionExit(id: string): void {
		this._flashRegion(id);
	}

	public onExploreHover(center: Point): void {
		this._exploreBoost = 1.1;
	}

	public onExploreOut(department: DepartmentType): void {
		this._exploreBoost = 1;
	}

	/** Mirrors reset() — the debug scene has no intervals/animations to clear. */
	public resetIntervalsAndAnimations(): void {
		this.reset();
	}

	public destroy(): void {
		if (this._container) {
			this._container.parent?.removeChild(this._container);
			this._container.destroy({ children: true }); //mask graphics are children too
			this._container = null;
			this._sprites = null;
			this._departmentContainers = null;
			this._masks = null;
		}
	}

	//Private
	//-----------------------------------------------------------------------------------------------

	/** Which department the drag progress focuses, per config/webgl-home sceneBands. */
	private _focusFromProgress(progress: number): DebugDepartmentKey {
		const bands = webglHome.sceneBands;
		if (progress < bands.iconMax) return 'home';
		if (progress >= bands.screens.min && progress <= bands.screens.max) return 'screens';
		if (progress >= bands.spaces.min && progress <= bands.spaces.max) return 'spaces';
		if (progress > bands.systemsMin) return 'systems';
		return null;
	}

	/**
	 * Re-trace each masked department's clip shape from the tagged items'
	 * live sprite bounds (HomeItemGeometry.getMaskRegionItems — the item
	 * sprites are centre-anchored and parallax every tick, so the rects are
	 * redrawn every tick, exactly like HomeScreens.drawMask on the particles
	 * path). Items resolve lazily on the first tick: create() runs between
	 * the timeline's two item-creation passes.
	 */
	private _updateMasks(): void {
		MASKED_DEPARTMENTS.forEach((department) => {
			const mask = this._masks[department];

			if (!mask.items) {
				mask.items = this._geometry.getMaskRegionItems(department);
			}

			mask.graphic.clear();
			mask.graphic.beginFill(0xffffff);
			mask.items.forEach((item) => {
				const sprite = item.sprite;
				mask.graphic.drawRect(
					sprite.x - sprite.width / 2,
					sprite.y - sprite.height / 2,
					sprite.width,
					sprite.height
				);
			});
			mask.graphic.endFill();
		});
	}

	private _cutAlphas(alphaFor: (department: DebugDepartmentKey) => number): void {
		if (!this._sprites) {
			return;
		}
		DEBUG_DEPARTMENTS.forEach((department) => {
			this._sprites[department].alpha = alphaFor(department);
		});
	}

	private _flashRegion(id: string): void {
		const department = REGION_TO_DEPARTMENT[id];
		if (!department || !this._sprites) {
			return;
		}
		const sprite = this._sprites[department];
		sprite.tint = 0xffffff;
		this._flashedSprites.push(sprite);
	}
}
